import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { aitsApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Swords, Loader2, Clock, Trophy,
  CheckCircle2, Play, Crown, Medal, Target, ChevronRight,
} from 'lucide-react';

// Phase 36 — All-India Test Series student hub
export default function AITSHub() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: 'list' }); // list | attempt | result

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aitsApi.list(examId || null);
      setTests(r.tests || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load AITS');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  const startTest = async (t) => {
    try {
      const r = await aitsApi.start(t.test_id);
      setView({ mode: 'attempt', test: r });
    } catch (e) {
      toast.error(e.message || 'Cannot start');
    }
  };

  if (view.mode === 'attempt') {
    return <AITSAttempt test={view.test} onDone={(res) => setView({ mode: 'result', res, test: view.test })} />;
  }
  if (view.mode === 'result') {
    return <AITSResult test={view.test} result={view.res} onBack={() => { setView({ mode: 'list' }); load(); }} />;
  }

  const grouped = {
    live: tests.filter(t => t.live_status === 'live'),
    scheduled: tests.filter(t => t.live_status === 'scheduled'),
    closed: tests.filter(t => t.live_status === 'closed'),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="aits-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Swords className="w-5 h-5 text-red-500" />All-India Test Series
              </h1>
              <p className="text-xs text-muted-foreground">Scheduled pan-India tests · live rankings · predicted cutoff.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold">Exam:</span>
          <Select value={examId || 'all'} onValueChange={(v) => setExamId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-64" data-testid="aits-exam-select"><SelectValue placeholder="All exams" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All exams</SelectItem>
              {exams.map(e => <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
        ) : tests.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="aits-empty">
            <Swords className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold">No tests scheduled yet</p>
            <p className="text-sm text-muted-foreground">Check back when your exam's next AITS is announced.</p>
          </div>
        ) : (
          <>
            <Section label="Live now" tests={grouped.live} colour="rose" onStart={startTest} user={user} testIdPrefix="aits-live" />
            <Section label="Upcoming" tests={grouped.scheduled} colour="indigo" user={user} testIdPrefix="aits-upcoming" />
            <Section label="Past tests" tests={grouped.closed} colour="slate" user={user} testIdPrefix="aits-past" onStart={null} />
          </>
        )}
      </div>
    </div>
  );
}

function Section({ label, tests, colour, onStart, user, testIdPrefix }) {
  if (!tests.length) return null;
  const colorCls = { rose: 'text-rose-500', indigo: 'text-indigo-500', slate: 'text-slate-500' }[colour];
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-section`}>
      <h3 className={`text-sm font-bold uppercase tracking-wider ${colorCls}`}>{label} <span className="text-muted-foreground font-normal">· {tests.length}</span></h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {tests.map((t, i) => (
          <TestCard key={t.test_id} t={t} onStart={onStart} user={user} testId={`${testIdPrefix}-${i}`} />
        ))}
      </div>
    </div>
  );
}

function TestCard({ t, onStart, user, testId }) {
  const start = new Date(t.starts_at);
  const submitted = t.my_attempt?.submitted_at;
  return (
    <div className="p-4 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{t.title}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px]">{t.exam_name || t.exam_id}</Badge>
            <Badge variant="outline" className="text-[10px]">{t.duration_min} min</Badge>
            <Badge variant="outline" className="text-[10px]">{t.total_questions} Qs</Badge>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {t.live_status === 'live' && <span className="text-rose-500 font-semibold">● Live · </span>}
            {t.live_status === 'closed' ? 'Closed' : start.toLocaleString()}
          </p>
        </div>
        <div className="shrink-0">
          {submitted ? (
            <Badge className="bg-emerald-500 text-white text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />Done · {t.my_attempt.score}</Badge>
          ) : t.live_status === 'live' && onStart ? (
            <Button size="sm" onClick={() => onStart(t)} data-testid={`${testId}-start`} className="bg-red-500 hover:bg-red-600 text-white">
              <Play className="w-3.5 h-3.5 mr-1.5" />Start
            </Button>
          ) : t.live_status === 'scheduled' ? (
            <Badge variant="outline" className="text-[10px]"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">Closed</Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function AITSAttempt({ test, onDone }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(test.total_questions).fill(-1));
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(test.duration_min * 60);
  const endRef = useRef(Date.now() + test.duration_min * 60 * 1000);

  useEffect(() => {
    const id = setInterval(() => {
      const s = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
      setRemaining(s);
      if (s <= 0) {
        clearInterval(id);
        submit();
      }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await aitsApi.submit(test.test_id, answers);
      onDone(r);
    } catch (e) {
      toast.error(e.message || 'Submit failed');
      setSubmitting(false);
    }
  }, [answers, submitting, onDone, test.test_id]);

  const q = test.questions[idx];
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold" data-testid="aits-attempt-title">{test.title}</p>
            <p className="text-[11px] text-muted-foreground">{test.exam_name} · Q {idx + 1} of {test.total_questions}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`${remaining < 300 ? 'bg-rose-500' : 'bg-indigo-500'} text-white`} data-testid="aits-timer">
              <Clock className="w-3 h-3 mr-1" />{String(min).padStart(2, '0')}:{String(sec).padStart(2, '0')}
            </Badge>
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="aits-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}Submit
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-5">
        <div className="p-6 rounded-2xl border border-border bg-card" data-testid="aits-question">
          <Badge variant="outline" className="text-[10px] mb-2">{q?.subject || 'General'}</Badge>
          <p className="font-semibold mb-4 text-sm leading-relaxed whitespace-pre-wrap">{q?.q}</p>
          <div className="space-y-2">
            {q?.options?.map((opt, oi) => (
              <button
                key={oi}
                onClick={() => {
                  const next = [...answers];
                  next[idx] = next[idx] === oi ? -1 : oi;
                  setAnswers(next);
                }}
                data-testid={`aits-option-${oi}`}
                className={`w-full text-left p-3 rounded-xl border text-sm transition ${answers[idx] === oi ? 'border-red-500 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-100' : 'border-border hover:border-red-300'}`}
              >
                <span className="font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-5">
            <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="aits-prev">
              ← Previous
            </Button>
            <Button size="sm" disabled={idx === test.total_questions - 1} onClick={() => setIdx(idx + 1)} data-testid="aits-next">
              Next →
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-2xl border border-border bg-card" data-testid="aits-palette">
          <p className="text-xs font-semibold mb-2">Question palette</p>
          <div className="grid grid-cols-5 gap-1.5">
            {answers.map((a, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`aits-palette-${i}`}
                className={`aspect-square rounded text-[11px] font-bold transition ${a !== -1 ? 'bg-emerald-500 text-white' : i === idx ? 'border-2 border-red-500 text-red-600' : 'border border-border text-muted-foreground hover:border-red-300'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">+{4} correct · −{1} wrong · 0 unattempted</p>
        </div>
      </div>
    </div>
  );
}

function AITSResult({ test, result, onBack }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    aitsApi.leaderboard(test.test_id).then(r => { setLeaderboard(r.leaderboard || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [test.test_id]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="aits-result-back"><ArrowLeft className="w-4 h-4 mr-1" />Back to tests</Button>

        <div className="p-6 rounded-2xl border border-border bg-gradient-to-br from-emerald-50 to-indigo-50 dark:from-emerald-950/20 dark:to-indigo-950/20" data-testid="aits-result">
          <h1 className="text-xl font-bold">{test.title}</h1>
          <p className="text-sm text-muted-foreground">Your pan-India performance</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <ResultStat label="Score" value={`${result.score}/${result.max_score}`} icon={Trophy} color="amber" testId="aits-result-score" />
            <ResultStat label="Rank" value={`#${result.rank}`} hint={`of ${result.total_attempts}`} icon={Crown} color="rose" testId="aits-result-rank" />
            <ResultStat label="Percentile" value={`${result.percentile}%`} icon={Medal} color="emerald" testId="aits-result-percentile" />
            <ResultStat label="Predicted Cutoff" value={result.predicted_cutoff} hint="top-10% avg" icon={Target} color="indigo" testId="aits-result-cutoff" />
          </div>
          <div className="flex items-center gap-4 text-xs mt-4 text-muted-foreground">
            <span>✅ Correct: <b className="text-emerald-600">{result.correct_count}</b></span>
            <span>❌ Wrong: <b className="text-rose-600">{result.negative_count}</b></span>
            <span>⏺ Unattempted: <b>{result.unattempted}</b></span>
          </div>
        </div>

        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="aits-leaderboard">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Leaderboard</h3>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="space-y-1">
              {leaderboard.map((row, i) => (
                <div
                  key={row.user_id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border text-xs ${row.is_me ? 'border-red-400 bg-red-50/50 dark:bg-red-950/30 font-semibold' : 'border-border'}`}
                  data-testid={`aits-lb-${i}`}
                >
                  <span className="w-8 text-center font-bold">
                    {row.rank === 1 ? <Crown className="w-4 h-4 mx-auto text-amber-500" />
                      : row.rank <= 3 ? <Medal className="w-4 h-4 mx-auto text-slate-400" />
                      : `#${row.rank}`}
                  </span>
                  <span className="flex-1 truncate">{row.display_name}{row.is_me ? ' (You)' : ''}</span>
                  <span className="text-muted-foreground">{row.correct_count}✓ / {row.negative_count}✗</span>
                  <span className="font-bold">{row.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultStat({ label, value, hint, icon: Icon, color, testId }) {
  const cls = {
    amber: 'bg-amber-100 dark:bg-amber-950/40 text-amber-500',
    rose: 'bg-rose-100 dark:bg-rose-950/40 text-rose-500',
    emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-500',
    indigo: 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-500',
  }[color];
  return (
    <div className="p-3 rounded-xl bg-white/70 dark:bg-background/50 border border-border" data-testid={testId}>
      <div className={`w-8 h-8 rounded-lg ${cls} flex items-center justify-center mb-1.5`}><Icon className="w-4 h-4" /></div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-bold">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
